import * as yup from 'yup'
import type {Maybe} from 'yup'
import moment from 'moment'

type FileType = {
  url?: string
  filename?: string
  file?: string
}

// FormErrorsの動作カスタマイズ用メタデータ
interface FormErrorsMeta {
  // 配列要素の順序を表す文言の書式。`$number` は添字に置換される。 (例: 'の$number番目')
  ordinalFormat?: string
}

yup.setLocale({
  mixed: {
    required: ({path, spec: {meta}}) => {
      if (meta?.file || meta?.select) {
        return `${path}を選択してください`
      } else {
        return `${path}を入力してください`
      }
    },
  },
  string: {
    email: ({path}) => `${path}が正しくありません`,
    min: ({path, min}) => `${path}を${min}文字以上で入力してください`,
    max: ({path, max}) => `${path}を${max}文字以内で入力してください`,
  },
})

// 労務HRのvalidators.js email関数の正規表現で上書き。yup標準のemailルールより緩い。
yup.addMethod(yup.string, 'email', function validateEmail(message = yup.defaultLocale.string?.email) {
  return this.matches(/^([^@\s]+)@((?:[a-zA-Z0-9_-]+\.)+[a-zA-Z]{2,})$/i, {
    message,
    name: 'email',
    excludeEmptyString: true,
  })
})

const dateValidator = (value?: string) => {
  if (!value) return true
  const date = moment(value, 'YYYY/MM/DD')

  return date.isValid() && /^\d{4}[/-]\d{1,2}[/-]\d{1,2}$/.test(value)
}

const maxLinesValidator = (value: Maybe<string>, max: number) => {
  if (!value) return true
  return value.split(/\r\n|\r|\n/).length <= max
}

yup.addMethod<yup.StringSchema>(yup.string, 'dateFormat', function dateFormat() {
  return this.test(
    'dateFormat',
    ({label}) => `${label}の入力形式が正しくありません`,
    (value?: string) => {
      if (!value) return true
      return dateValidator(value)
    }
  )
})

yup.addMethod<yup.StringSchema>(yup.string, 'maxLines', function maxLines(max: number) {
  return this.test(
    'max-lines',
    ({label}) => `${label}は${max}行以内で入力してください`,
    (value) => maxLinesValidator(value, max)
  )
})

yup.addMethod<yup.StringSchema>(yup.string, 'numeric', function numeric() {
  return this.matches(/^[0-9]+$/, {
    message: ({label}) => `${label}を半角数字で入力してください`,
    name: 'numeric',
    excludeEmptyString: true,
  })
})

yup.addMethod<yup.StringSchema>(yup.string, 'zenkakuKatakanaWithSpace', function zenkakuKatakanaWithSpace() {
  return this.matches(/^[\u30A0-\u30FF\u3000 ]+$/, {
    message: ({label}) => `${label}を全角カタカナとスペースで入力してください`,
    name: 'zenkakuKatakanaWithSpace',
    excludeEmptyString: true,
  })
})

yup.addMethod<yup.StringSchema>(yup.string, 'emptyToNull', function emptyToNull() {
  return this.transform((value) => (value === '' ? null : value))
})

yup.addMethod<yup.NumberSchema>(yup.number, 'range', function range(min: number, max: number) {
  return this.test(
    'range',
    ({label}) => `${label}を${min}から${max}までの半角数字で入力してください`,
    (value?: number) => {
      if (value === undefined || value === null) return true
      return value >= min && value <= max
    }
  )
})

yup.addMethod<yup.NumberSchema>(yup.number, 'allowEmptyString', function allowEmptyString() {
  return this.transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
})

const MAX_FILE_SIZE = 1024 * 1024 * 10

const maxFileSizeValidator = (value: File | undefined, max: number) => {
  if (!value) return true
  return value.size < max
}

yup.addMethod<yup.MixedSchema>(yup.mixed, 'fileSize', function fileSize() {
  return this.file().test(
    'fileSize',
    ({label}) => `${label}にアップロードできるファイル容量は10MB迄です。ご確認の上、再度アップロードください。`,
    (value?: FileType | File) => {
      if (!value) return true
      if (!(value instanceof File)) return true

      return maxFileSizeValidator(value, MAX_FILE_SIZE)
    }
  )
})

yup.addMethod<yup.MixedSchema>(yup.mixed, 'file', function file() {
  return this.meta({file: true})
})

yup.addMethod(yup.Schema, 'formErrorsMeta', function formErrorsMeta(options: FormErrorsMeta) {
  return this.meta({formErrors: options})
})

declare module 'yup' {
  interface Schema {
    formErrorsMeta(options: FormErrorsMeta): this
  }
  interface StringSchema<TType extends Maybe<string> = string | undefined> {
    dateFormat(): StringSchema<TType>
    maxLines(max: number): StringSchema<TType>
    numeric(): StringSchema<TType>
    emptyToNull(): StringSchema<TType>
    zenkakuKatakanaWithSpace(): StringSchema<TType>
  }
  interface NumberSchema {
    range(min: number, max: number): this
    allowEmptyString(): this
  }
  interface MixedSchema {
    fileSize(): this
    file(): this
  }
  interface CustomSchemaMetadata {
    file?: boolean
    formErrors?: FormErrorsMeta
  }
}

export {yup}
export type * from 'yup'
